import {ReactNode, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {useService} from '@yosmanyga/di';
import {Button, Container, I18NText, Icon, Input, Text, useIcons,} from '@yosmanyga/rich-ui';
import {NotesController, ProfileController} from '../../controller';
import {Basic} from "../Payment/Basic";

const Notes = observer(({
    templates,
    onSend
}: NotesProps) => {
    const icons = useIcons();

    const profileController = useService(ProfileController, []);
    const notesController = useService(NotesController, []);

    useEffect(() => {
        (async () => {
            await profileController.load();
            await notesController.load();
        })();
    }, []);

    useEffect(() => {
        onSend?.();
    }, [notesController.notes, onSend]);

    const ListComponent = observer(() => {
        if (
            !profileController.loaded
            || !notesController.loaded
        ) {
            return templates.layout.progress;
        }

        return notesController.notes.length > 0
            ? notesController.notes.map(({id, text}, i) => {
                return <Container
                    key={i}
                    flow="row"
                    align={{
                        main: 'space-between',
                    }}
                    margin={{
                        top: i !== 0 ? 2 : undefined,
                    }}
                >
                    <Text
                        flex={{
                            grow: 1,
                            shrink: 1,
                        }}
                    >
                        {text}
                    </Text>
                    <Button
                        onClick={async () => {
                            await notesController.delete(id);
                        }}
                    >
                        <Icon
                            component={icons.actions.remove}
                            size='xs'
                        />
                    </Button>
                </Container>
            })
            : <Container flow="column">
                <I18NText>You don't have notes</I18NText>
            </Container>
    }, [])

    const InputComponent = observer(() => {
        if (
            !profileController.loaded
            || !notesController.loaded
        ) {
            return templates.layout.progress;
        }

        return <>
            <I18NText>Save your notes</I18NText>
            <Container
                flow="row"
                align={{
                    cross: 'center',
                }}
                margin={{
                    top: 1
                }}
            >
                <Input
                    flex={{
                        grow: 1,
                        shrink: 1,
                    }}
                    value={notesController.input}
                    onChange={(value) => {
                        notesController.prepare(value);
                    }}
                    onEnter={async () => {
                        await notesController.send();

                        onSend?.();
                    }}
                    capitalize="sentences"
                />
                <Button
                    margin={{
                        left: 1,
                    }}
                    progress={notesController.sending}
                    disabled={notesController.input === ''}
                    onClick={async () => {
                        await notesController.send();

                        onSend?.();
                    }}
                >
                    <Icon
                        component={icons.actions.send}
                        size='xs'
                    />
                </Button>
            </Container>
        </>
    }, [])

    if (profileController.isBasic()) {
        return <Basic
            templates={{
                layout: ({children}) => {
                    return <templates.layout
                        list={children}
                    />
                }
            }}
            onGoToPayment={() => {
                //
            }}
        />
    }

    return <templates.layout
        list={<ListComponent />}
        input={<InputComponent />}
    />
});

type NotesProps = {
    templates: {
        layout: (
            params: {
                progress?: boolean;
                list?: ReactNode;
                input?: ReactNode;
            }
        ) => ReactNode;
    };
    onSend?: () => void;
};

export type {
    NotesProps,
}

export {
    Notes
};
